import { c as _c } from "react/compiler-runtime";
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tab, Tabs } from '@repo/design-system/components/ds/tabs/tabs';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import { useAppDispatch } from '@repo/store/maps/hooks';
import { setSelectedRegionId } from '@repo/store/maps/slices/mapData';
import type { MapDataRegion } from '@repo/types/maps/map-types';
import { ColorPicker } from '@repo/ui/components/color-picker/color-picker';
import { useEffect, useRef, useState } from 'react';
import { CategoryColorPalette } from '../creator-tools/components/category-color-palette';
import { useRegionOperations } from '../game-map/hooks/useRegionOperations';
const REGION_LINE_THICKNESS = {
  region: 4,
  subregion: 2
} as const;
export const RegionInfoEditing = t0 => {
  const $ = _c(91);
  const {
    region
  } = t0;
  const dispatch = useAppDispatch();
  const titleInputRef = useRef(null);
  const {
    updateRegionProperties,
    deleteRegionById,
    editRegionGeometry,
    stopEditingGeometry,
    isUpdating,
    isDeleting
  } = useRegionOperations();
  const [title, setTitle] = useState(region.properties.title || "");
  const [color, setColor] = useState(region.properties.color || "#3b82f6");
  const [regionType, setRegionType] = useState(region.properties.lineThickness === REGION_LINE_THICKNESS.region ? "region" : "subregion");
  let t1;
  if ($[0] !== region.properties.minZoom) {
    t1 = region.properties.minZoom?.toString() || "";
    $[0] = region.properties.minZoom;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [minZoom, setMinZoom] = useState(t1);
  let t2;
  if ($[2] !== region.properties.maxZoom) {
    t2 = region.properties.maxZoom?.toString() || "";
    $[2] = region.properties.maxZoom;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [maxZoom, setMaxZoom] = useState(t2);
  let t3;
  if ($[4] !== region.properties.color || $[5] !== region.properties.lineThickness || $[6] !== region.properties.maxZoom || $[7] !== region.properties.minZoom || $[8] !== region.properties.title) {
    t3 = () => {
      setTitle(region.properties.title || "");
      setColor(region.properties.color || "#3b82f6");
      setRegionType(region.properties.lineThickness === REGION_LINE_THICKNESS.region ? "region" : "subregion");
      setMinZoom(region.properties.minZoom?.toString() || "");
      setMaxZoom(region.properties.maxZoom?.toString() || "");
    };
    $[4] = region.properties.color;
    $[5] = region.properties.lineThickness;
    $[6] = region.properties.maxZoom;
    $[7] = region.properties.minZoom;
    $[8] = region.properties.title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== region) {
    t4 = [region];
    $[10] = region;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== color || $[13] !== maxZoom || $[14] !== minZoom || $[15] !== region.geometry || $[16] !== region.properties.externalId || $[17] !== regionType || $[18] !== stopEditingGeometry || $[19] !== title || $[20] !== updateRegionProperties) {
    t5 = async () => {
      await updateRegionProperties(region.properties.externalId, {
        title,
        color,
        lineThickness: REGION_LINE_THICKNESS[regionType],
        minZoom: minZoom ? parseFloat(minZoom) : undefined,
        maxZoom: maxZoom ? parseFloat(maxZoom) : undefined
      }, region.geometry);
      stopEditingGeometry();
    };
    $[12] = color;
    $[13] = maxZoom;
    $[14] = minZoom;
    $[15] = region.geometry;
    $[16] = region.properties.externalId;
    $[17] = regionType;
    $[18] = stopEditingGeometry;
    $[19] = title;
    $[20] = updateRegionProperties;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const handleSave = t5;
  let t6;
  if ($[22] !== deleteRegionById || $[23] !== dispatch || $[24] !== region.properties.externalId || $[25] !== region.properties.title || $[26] !== stopEditingGeometry) {
    t6 = async () => {
      if (confirm(`Delete region "${region.properties.title || "Unnamed"}"?`)) {
        await deleteRegionById(region.properties.externalId);
        stopEditingGeometry();
        dispatch(setSelectedRegionId(null));
      }
    };
    $[22] = deleteRegionById;
    $[23] = dispatch;
    $[24] = region.properties.externalId;
    $[25] = region.properties.title;
    $[26] = stopEditingGeometry;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const handleDelete = t6;
  let t7;
  if ($[28] !== editRegionGeometry || $[29] !== region) {
    t7 = () => {
      editRegionGeometry(region);
    };
    $[28] = editRegionGeometry;
    $[29] = region;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const handleEditShape = t7;
  let t8;
  if ($[31] !== dispatch || $[32] !== stopEditingGeometry) {
    t8 = () => {
      stopEditingGeometry();
      dispatch(setSelectedRegionId(null));
    };
    $[31] = dispatch;
    $[32] = stopEditingGeometry;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const handleClose = t8;
  let t10;
  let t9;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = css.raw({
      padding: "4",
      paddingBottom: "12"
    });
    t10 = <Type variant="header-s" color="white">Edit Region</Type>;
    $[34] = t10;
    $[35] = t9;
  } else {
    t10 = $[34];
    t9 = $[35];
  }
  let t11;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <IconButton color="secondary" size="sm" aria-label="More options" className={css({
      marginLeft: "auto"
    })}><Icon icon="more_vert" decorative={true} /></IconButton>;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== handleEditShape) {
    t12 = <DropdownMenuItem value="edit-shape" leadingIcon="edit" onSelect={handleEditShape}>Edit Shape</DropdownMenuItem>;
    $[37] = handleEditShape;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== handleDelete || $[40] !== isDeleting) {
    t13 = <DropdownMenuItem value="delete" leadingIcon="delete" onSelect={handleDelete} disabled={isDeleting} destructive={true}>Delete Region</DropdownMenuItem>;
    $[39] = handleDelete;
    $[40] = isDeleting;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t12 || $[43] !== t13) {
    t14 = <DropdownMenu trigger={t11}>{t12}{t13}</DropdownMenu>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Icon icon="close" decorative={true} />;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== handleClose) {
    t16 = <IconButton onClick={handleClose} color="secondary" size="sm" aria-label="Close">{t15}</IconButton>;
    $[46] = handleClose;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== t14 || $[49] !== t16) {
    t17 = <FlexRow align="center" gap="8" width="full" className={t9}>{t10}{t14}{t16}</FlexRow>;
    $[48] = t14;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = css({
      overflow: "hidden",
      display: "flex",
      flex: "1"
    });
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = css.raw({
      padding: "8"
    });
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e => setTitle(e.target.value);
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== title) {
    t21 = <TextInput ref={titleInputRef} value={title} onChange={t20} placeholder="Region Title" label="Title" size="sm" fullWidth={true} />;
    $[54] = title;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== color) {
    t22 = <FlexColumn gap="8"><ColorPicker label="Color" value={color} onChange={setColor} size="sm" /><CategoryColorPalette selectedColor={color} onColorSelect={setColor} label="Choose custom color, or one from palette" /></FlexColumn>;
    $[56] = color;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Type variant="body-s" color="text.secondary">Type</Type>;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  let t25;
  let t26;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = value => setRegionType(value as keyof typeof REGION_LINE_THICKNESS);
    t25 = <Tab value="region">Region</Tab>;
    t26 = <Tab value="subregion">Subregion</Tab>;
    $[59] = t24;
    $[60] = t25;
    $[61] = t26;
  } else {
    t24 = $[59];
    t25 = $[60];
    t26 = $[61];
  }
  let t27;
  if ($[62] !== regionType) {
    t27 = <FlexColumn gap="8">{t23}<Tabs role="radiogroup" fullWidth={true} aria-label="Type" value={regionType} onValueChange={t24}>{t25}{t26}</Tabs></FlexColumn>;
    $[62] = regionType;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = e_0 => setMinZoom(e_0.target.value);
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== minZoom) {
    t29 = <TextInput value={minZoom} onChange={t28} placeholder="Min Zoom" label="Min Zoom" type="number" size="sm" fullWidth={true} />;
    $[65] = minZoom;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = e_1 => setMaxZoom(e_1.target.value);
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== maxZoom) {
    t31 = <TextInput value={maxZoom} onChange={t30} placeholder="Max Zoom" label="Max Zoom" type="number" size="sm" fullWidth={true} />;
    $[68] = maxZoom;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t29 || $[71] !== t31) {
    t32 = <FlexRow gap="8" width="full">{t29}{t31}</FlexRow>;
    $[70] = t29;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t21 || $[74] !== t22 || $[75] !== t27 || $[76] !== t32) {
    t33 = <ScrollableContent maxHeight={600} className={t18}><FlexColumn gap="12" width="full" className={t19}>{t21}{t22}{t27}{t32}</FlexColumn></ScrollableContent>;
    $[73] = t21;
    $[74] = t22;
    $[75] = t27;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  const t34 = isUpdating ? "Saving..." : "Save";
  let t35;
  if ($[78] !== handleSave || $[79] !== isUpdating || $[80] !== t34) {
    t35 = <Button onClick={handleSave} color="success" size="sm" fullWidth={true} disabled={isUpdating}>{t34}</Button>;
    $[78] = handleSave;
    $[79] = isUpdating;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== handleClose) {
    t36 = <Button variant="ghost" onClick={handleClose} color="primary" size="sm" fullWidth={true}>Close</Button>;
    $[82] = handleClose;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  let t37;
  if ($[84] !== t35 || $[85] !== t36) {
    t37 = <FlexColumn gap="8" justify="end" width="full">{t35}{t36}</FlexColumn>;
    $[84] = t35;
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== t17 || $[88] !== t33 || $[89] !== t37) {
    t38 = <>{t17}{t33}{t37}</>;
    $[87] = t17;
    $[88] = t33;
    $[89] = t37;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  return t38;
};